import { Component, OnInit, inject } from '@angular/core';
import { Observable, throwError, Subject } from 'rxjs';
import { finalize, catchError, tap } from 'rxjs/operators';

import { TravelTimeImprovementSuggestionService } from './services/travel-time-improvement-suggestion.service';
import { TravelTimeImprovementSuggestionView } from './models/travel-time-improvement-suggestion-view.model';
import { TravelTimeImprovementSuggestionsView } from './models/travel-time-improvement-suggestions-view.model';

@Component({
    selector: 'app-travel-time-improvement-suggestions',
    templateUrl: './travel-time-improvement-suggestions.component.html',
    styleUrls: ['./travel-time-improvement-suggestions.component.scss'],
    standalone: false
})
export class TravelTimeImprovementSuggestionsComponent implements OnInit {
    private readonly travelTimeImprovementSuggestionService = inject(TravelTimeImprovementSuggestionService);

    public uncheckedAny: boolean;
    public uncheckedAll: boolean;
    public loading: boolean;
    public editing: boolean;
    public timeWindowSmaller: boolean;
    public suggestions$: Observable<TravelTimeImprovementSuggestionsView>;
    public confirm$: Subject<boolean>;
    public cancel$: Subject<boolean>;

    private handledAny: boolean;

    constructor() {
        this.confirm$ = new Subject();
        this.cancel$ = new Subject();
    }

    ngOnInit(): void {
        this.suggestions$ = this.travelTimeImprovementSuggestionService
            .getSuggestions()
            .pipe(catchError(() => throwError('Get suggestions failed.')));
    }

    public dismissModal(): void {
        if (!this.loading) {
            this.cancel$.next(this.handledAny);
        }
    }

    public checkboxChanged(
        suggestions: TravelTimeImprovementSuggestionView[],
        suggestion: TravelTimeImprovementSuggestionView
    ): void {
        this.uncheckedAny = suggestions.some(item => !item.checked);
        this.uncheckedAll = !suggestions.some(item => item.checked);
        suggestion.permanent = suggestion.checked;
    }

    public edit(suggestion: TravelTimeImprovementSuggestionView, event: Event): void {
        if (!this.editing && suggestion.checked) {
            event.stopPropagation();
            this.editing = true;
            suggestion.editing = true;
        }
    }

    public editingChange(editing: boolean, suggestions: TravelTimeImprovementSuggestionView[]): void {
        this.editing = editing;
        this.timeWindowSmaller = suggestions.some(suggestion => suggestion.newTimeWindowSmaller);
    }

    public accept(suggestions: TravelTimeImprovementSuggestionView[]): void {
        this.loading = true;
        this.suggestions$ = this.travelTimeImprovementSuggestionService.acceptSuggestions(suggestions).pipe(
            catchError(() => throwError('Accept suggestions failed.')),
            tap(result => {
                this.handledAny = true;
                if (!result.suggestions || result.suggestions.length === 0) {
                    this.confirm$.next(this.handledAny);
                }
            }),
            finalize(() => {
                this.resetState();
            })
        );
    }

    public rejectAll(suggestions: TravelTimeImprovementSuggestionView[]): void {
        this.loading = true;
        this.suggestions$ = this.travelTimeImprovementSuggestionService
            .rejectAllCurrentAndGetNewSuggestions(suggestions)
            .pipe(
                catchError(() => throwError('Reject suggestions failed.')),
                tap(result => {
                    this.handledAny = true;
                    if (!result.suggestions || result.suggestions.length === 0) {
                        this.confirm$.next(this.handledAny);
                    }
                }),
                finalize(() => {
                    this.resetState();
                })
            );
    }

    private resetState(): void {
        this.timeWindowSmaller = false;
        this.uncheckedAny = false;
        this.loading = false;
    }
}
